/**
 * Forward Fibonacci ratio relationships between pivots, measured in BARS (time), not price.
 *
 * For a base pair of pivots (P1, P2) spanning D bars, the question is whether other pivots land at
 * Fibonacci fractions and extensions of D, measured forward from P1:
 *
 *   P1                    P2
 *   |----0.382D----|------|----------1.618D--------->
 *        ^                            ^
 *        intermediate pivot           projected pivot
 *
 * Ratios below 1 fall between the two anchors; ratios above 1 project beyond P2. Both are measured
 * from P1, so a match means "this pivot sits at 0.618 of the base span", which is the claim the
 * label makes.
 */

/** Ratios checked against every base pair. */
export const FIB_TIME_RATIOS = [0.236, 0.382, 0.5, 0.618, 0.786, 1.272, 1.618, 2.618];

/** Base pairs shorter than this are noise; longer than this stop being one swing. */
export const MIN_WINDOW = 20;
export const MAX_WINDOW = 300;

/** Bars of slack when matching a computed offset against a real pivot. */
export const DEFAULT_TOLERANCE = 1;

/** Matches required before a base pair is worth drawing. */
export const DEFAULT_MIN_MATCHES = 2;

/**
 * Ratios projected FORWARD past the last bar, where no pivot exists yet.
 *
 * A superset of the matching ratios: 1.0 (the span repeated once) and 4.236 are included because a
 * projection is a target rather than a confirmation, and the far extensions are exactly the ones
 * worth knowing about in advance. They are not used for matching, where a target beyond the data
 * could never find a pivot anyway.
 */
export const FIB_PROJECTION_RATIOS = [0.618, 1.0, 1.272, 1.618, 2.618, 4.236];

/**
 * Fibonacci targets that fall BEYOND the last bar with price on it.
 *
 * These are the incomplete setups: a base pair whose next ratio lands in the future, where a pivot
 * would complete the sequence if one forms. Nothing is asserted about whether it will — the marker
 * says where to look, which is why the label carries PROJ rather than the bare ratio a confirmed
 * match gets.
 *
 * Targets landing on or before `lastRealIndex` are dropped: a ratio inside the price history is
 * either already a match or already a miss, and either way it is not a future target.
 *
 * @param {Array} results     pairs from findFibonacciPivotPairs
 * @param {number} lastRealIndex index of the last bar carrying price
 * @param {number} maxIndex   last addressable bar, including padded future space
 * @returns {Array<{index:number, ratio:number, label:string, p1:object, p2:object}>} nearest first
 */
export function projectedTargets(results, lastRealIndex, maxIndex, ratios = FIB_PROJECTION_RATIOS) {
  if (!Array.isArray(results) || !Number.isFinite(lastRealIndex) || !Number.isFinite(maxIndex)) return [];

  const seen = new Set();
  const out = [];
  results.forEach(res => {
    if (!res || !res.p1 || !Number.isFinite(res.distance)) return;
    ratios.forEach(ratio => {
      const index = Math.round(res.p1.index + ratio * res.distance);
      // Past the price history, but still on the chart.
      if (index <= lastRealIndex || index > maxIndex) return;
      // Two pairs often project onto the same bar at the same ratio; one marker is enough.
      const key = `${index}|${ratio}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        index,
        ratio,
        label: `PROJ ${formatRatio(ratio)}`,
        barsAhead: index - lastRealIndex,
        p1: res.p1,
        p2: res.p2,
      });
    });
  });

  // Nearest first: the soonest target is the actionable one.
  return out.sort((a, b) => a.index - b.index || a.ratio - b.ratio);
}

/** Formats a ratio for the on-chart label: 0.618, 1.618 — trailing zeros trimmed. */
export function formatRatio(ratio) {
  if (typeof ratio !== 'number' || !Number.isFinite(ratio)) return '';
  return String(parseFloat(ratio.toFixed(3)));
}

/**
 * Finds Fibonacci-related pivots for every qualifying base pair.
 *
 * Every ordered combination is evaluated — high-high, low-low, high-low and low-high — because a
 * time relationship does not care which kind of extreme sits at each end; only the bar count does.
 *
 * @param {Array<{index:number,type:string,price:number}>} pivots ascending by index
 * @param {object} [opts]
 * @returns {Array<{p1:object, p2:object, distance:number, matches:Array}>} most matches first
 */
export function findFibonacciPivotPairs(pivots, opts = {}) {
  const minWindow = opts.minWindow ?? MIN_WINDOW;
  const maxWindow = opts.maxWindow ?? MAX_WINDOW;
  const tolerance = opts.tolerance ?? DEFAULT_TOLERANCE;
  const minMatches = opts.minMatches ?? DEFAULT_MIN_MATCHES;
  const ratios = opts.ratios ?? FIB_TIME_RATIOS;

  if (!Array.isArray(pivots) || pivots.length < 3) return [];
  const ordered = [...pivots]
    .filter(p => p && typeof p.index === 'number')
    .sort((a, b) => a.index - b.index);

  // Bar index -> pivot. Where a bar is both a swing high and a swing low the high wins, which is
  // arbitrary but stable; such bars are rare and either answer is a pivot at that bar.
  const byIndex = new Map();
  for (const p of ordered) {
    if (!byIndex.has(p.index)) byIndex.set(p.index, p);
  }

  const results = [];

  for (let i = 0; i < ordered.length; i++) {
    for (let j = i + 1; j < ordered.length; j++) {
      const p1 = ordered[i];
      const p2 = ordered[j];
      const distance = p2.index - p1.index;
      // Left to right, so distance is always positive; the window bounds do the rest.
      if (distance < minWindow) continue;
      if (distance > maxWindow) break;   // ordered by index, so every later j is further still

      const matches = [];

      ratios.forEach(ratio => {
        const target = p1.index + ratio * distance;
        let best = null;
        let bestDelta = Infinity;
        // Probing the few bar indices around the target instead of scanning every pivot. The scan
        // was O(pivots) per ratio per pair, which on a full-history chart meant minutes of blocked
        // main thread; the answer is identical, since only pivots within tolerance could ever win.
        const lo = Math.ceil(target - tolerance);
        const hi = Math.floor(target + tolerance);
        for (let idx = lo; idx <= hi; idx++) {
          // The anchors themselves are not matches — they define the span rather than confirming
          // it. Without this, ratio 1.0-adjacent targets would "find" P2 and every pair would
          // report a hit it did not earn.
          if (idx === p1.index || idx === p2.index) continue;
          const cand = byIndex.get(idx);
          if (!cand) continue;
          const delta = Math.abs(idx - target);
          if (delta < bestDelta) {
            best = cand;
            bestDelta = delta;
          }
        }
        if (best) {
          matches.push({
            ratio,
            label: formatRatio(ratio),
            targetIndex: target,
            pivot: best,
            delta: bestDelta,
            isProjection: ratio > 1,
          });
        }
      });

      if (matches.length >= minMatches) {
        results.push({ p1, p2, distance, matches, matchCount: matches.length });
      }
    }
  }

  // Most matches first: a pair confirmed at four ratios is a stronger claim than one confirmed at
  // two, and callers drawing a limited number should take the strongest.
  return results.sort((a, b) => b.matchCount - a.matchCount || a.distance - b.distance);
}

/**
 * Flattens results into one dot per participating pivot, ready to render.
 *
 * A pivot can participate in several pairs at different ratios. Rather than stacking a dot per
 * occurrence in the same spot, each pivot appears once with its ratios joined — otherwise the
 * densest and most interesting pivots would be the ones drawn as an unreadable pile.
 */
export function pivotMarkersFrom(results, { includeAnchors = false } = {}) {
  const byIndex = new Map();
  (results || []).forEach(res => {
    // P1 is where each measurement starts, so it is labelled 0. Without it the ratios float free:
    // a "0.618" dot says nothing unless you can see which bar it is 0.618 of the way from.
    if (includeAnchors && res.p1) {
      if (!byIndex.has(res.p1.index)) byIndex.set(res.p1.index, { pivot: res.p1, ratios: [] });
      const anchor = byIndex.get(res.p1.index);
      if (!anchor.ratios.includes('0')) anchor.ratios.unshift('0');
    }
    (res.matches || []).forEach(m => {
      const key = m.pivot.index;
      if (!byIndex.has(key)) {
        byIndex.set(key, { pivot: m.pivot, ratios: [] });
      }
      const entry = byIndex.get(key);
      if (!entry.ratios.includes(m.label)) entry.ratios.push(m.label);
    });
  });

  return [...byIndex.values()]
    .map(({ pivot, ratios }) => ({
      index: pivot.index,
      type: pivot.type,
      price: pivot.price,
      ratios,
      label: ratios.join(' / '),
    }))
    .sort((a, b) => a.index - b.index);
}

export default findFibonacciPivotPairs;
